import { CustomTooltip } from '@/components/custom-tooltip';
import { Button } from '@/components/ui/button';
import { clsx } from 'clsx';
import { FolderOpen, Layers, RefreshCw, Trash2 } from 'lucide-react';
import { useFormContext } from 'react-hook-form';

interface FormSubmitBarProps {
	handleReset: () => void;
	showAdvanced: boolean;
	toggleAdvanced: () => void;
	onLoad?: () => void;
	onDelete?: () => void;
	isSaveGroupButtonDisabled?: boolean;
}

export function FormSubmitBar({
	handleReset,
	showAdvanced,
	toggleAdvanced,
	onLoad,
	onDelete,
	isSaveGroupButtonDisabled = false,
}: FormSubmitBarProps) {
	const form = useFormContext();

	const { isSubmitting } = form.formState;

	return (
		<div className='tw-flex tw-flex-col tw-items-start tw-relative'>
			<div className='tw-flex tw-items-center tw-gap-2 '>
				<CustomTooltip
					backgroundColor='tw-bg-white'
					arrowColor='tw-fill-primary'
					arrow
					borderColor='tw-border-primary'
					content={
						isSaveGroupButtonDisabled ? (
							<p className='tw-text-[hsl(var(--foreground))]'>
								{'To save group, Please enter a group name'}
							</p>
						) : (
							<p className='tw-text-[hsl(var(--foreground))]'>{'Save Group'}</p>
						)
					}
				>
					<span className='tw-inline-block'>
						<Button
							type='submit'
							className={clsx(isSubmitting && 'tw-opacity-50')}
							disabled={isSaveGroupButtonDisabled}
						>
							{isSubmitting ? 'Saving...' : 'Save Group'}
						</Button>
					</span>
				</CustomTooltip>

				<CustomTooltip
					content={
						<p className='tw-text-[hsl(var(--foreground))]'>{'Reset Group'}</p>
					}
					backgroundColor='tw-bg-white'
					arrowColor='tw-fill-primary'
					arrow
					borderColor='tw-border-primary'
					textColor='tw-text-[hsl(var(--foreground))]'
				>
					<Button
						type='button'
						variant='outline'
						size='icon'
						onClick={handleReset}
						aria-label='Reset Group'
						className='tw-p-2 tw-h-10 tw-w-10 tw-flex tw-items-center tw-justify-center'
					>
						<RefreshCw className='tw-size-4 tw-self-center' />
					</Button>
				</CustomTooltip>

				<CustomTooltip
					content={
						<p className='tw-text-[hsl(var(--foreground))]'>{'Load Group'}</p>
					}
					backgroundColor='tw-bg-white'
					arrowColor='tw-fill-primary'
					arrow
					borderColor='tw-border-primary'
					textColor='tw-text-[hsl(var(--foreground))]'
				>
					<Button
						type='button'
						variant='outline'
						size='icon'
						aria-label='Load Group'
						onClick={onLoad}
					>
						<FolderOpen className='tw-size-4 tw-self-center' />
					</Button>
				</CustomTooltip>

				<CustomTooltip
					content={
						<p className='tw-text-[hsl(var(--foreground))]'>{'Delete Group'}</p>
					}
					backgroundColor='tw-bg-white'
					arrowColor='tw-fill-primary'
					arrow
					borderColor='tw-border-primary'
					textColor='tw-text-[hsl(var(--foreground))]'
				>
					<Button
						type='button'
						variant='outline'
						size='icon'
						aria-label='Delete Group'
						onClick={onDelete}
					>
						<Trash2 className='tw-size-4 tw-self-center' />
					</Button>
				</CustomTooltip>

				<CustomTooltip
					backgroundColor='tw-bg-white'
					arrowColor='tw-fill-primary'
					arrow
					borderColor='tw-border-primary'
					textColor='tw-text-[hsl(var(--foreground))]'
					content={
						<p className='tw-text-[hsl(var(--foreground))]'>
							{showAdvanced
								? 'Switch to simple view'
								: 'Switch to advanced view'}
						</p>
					}
				>
					<Button
						type='button'
						variant='outline'
						size='icon'
						onClick={toggleAdvanced}
						aria-label={
							showAdvanced ? 'Switch to simple view' : 'Switch to advanced view'
						}
						aria-pressed={showAdvanced}
					>
						<Layers className='tw-size-4 tw-self-center' />
					</Button>
				</CustomTooltip>
			</div>
		</div>
	);
}
